import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Autocomplete,
  Chip,
  FormHelperText,
  Box,
  Typography,
  IconButton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';

// Space types options
const SPACE_TYPES = [
  'Banquet Hall',
  'Conference Room',
  'Outdoor Area',
  'Rooftop',
  'Ballroom',
  'Theater',
  'Classroom',
  'Studio',
  'Garden',
  'Lounge',
  'Gallery'
];

// Features options
const FEATURES = [
  'Wifi',
  'Audio System',
  'Projector',
  'Stage',
  'Natural Light',
  'Air Conditioning',
  'Catering Kitchen',
  'Bar',
  'Accessible',
  'Parking',
  'Restrooms',
  'Private Entrance',
  'Tables & Chairs',
  'Outdoor Access'
];

// Ideal Events options
const IDEAL_EVENTS = [
  'Wedding',
  'Corporate Meeting',
  'Conference',
  'Birthday Party',
  'Anniversary',
  'Graduation',
  'Holiday Party',
  'Networking Event',
  'Product Launch',
  'Exhibition',
  'Workshop',
  'Seminar',
  'Retreat',
  'Gala',
  'Fashion Show',
  'Film Screening',
  'Performance',
  'Photo Shoot'
];

// Pricing Strategy options
const PRICING_STRATEGIES = [
  'Per Hour',
  'Per Day',
  'Per Half-Day',
  'Per Guest',
  'Per Event',
  'Other'
];

// Tech Available options
const TECH_AVAILABLE = [
  'A/V System',
  'Projector',
  'Screen',
  'Microphones',
  'Speakers',
  'DJ Equipment',
  'Lighting',
  'Video Conferencing',
  'Live Streaming',
  'WiFi',
  'High-Speed Internet',
  'Smart TV',
  'Recording Equipment',
  'Sound System'
];

/**
 * Form for adding or editing venue spaces
 */
const SpaceForm = ({ 
  open, 
  onClose, 
  onSubmit, 
  editSpace = null, 
  isSubmitting = false 
}) => {
  const initialState = {
    id: null,
    name: '',
    seatingCapacity: '',
    standingCapacity: '',
    floorplanImage: null,
    idealEvents: [],
    pricingOptions: [],
    images: [],
    techAvailable: []
  };

  const [space, setSpace] = useState(initialState);
  const [errors, setErrors] = useState({});
  const [previewImages, setPreviewImages] = useState([]);
  const [previewFloorplan, setPreviewFloorplan] = useState(null);
  
  const imagesInputRef = useRef(null);
  const floorplanInputRef = useRef(null);

  // Reset form when dialog opens and load data if editing
  useEffect(() => {
    if (open) {
      if (editSpace) {
        // Handle backward compatibility for spaces with old pricing format
        let spaceData = { ...initialState, ...editSpace };
        
        // If the space has the old pricing format (pricingStrategy and pricePerUnit),
        // convert it to the new format (pricingOptions array)
        if (editSpace.pricingStrategy && !editSpace.pricingOptions) {
          spaceData.pricingOptions = [{
            strategy: editSpace.pricingStrategy,
            price: editSpace.pricePerUnit || '',
            minHours: editSpace.minHoursRequired || '',
            minGuests: editSpace.minGuestsRequired || '',
            securityDeposit: editSpace.securityDeposit || '',
            description: ''
          }];
          
          // Remove the old properties to avoid conflicts
          delete spaceData.pricingStrategy;
          delete spaceData.pricePerUnit;
          delete spaceData.minHoursRequired;
          delete spaceData.minGuestsRequired;
          delete spaceData.securityDeposit;
        } else if (!spaceData.pricingOptions || spaceData.pricingOptions.length === 0) {
          // Ensure there's at least one empty pricing option
          spaceData.pricingOptions = [{ 
            strategy: '', 
            price: '',
            minHours: '',
            minGuests: '',
            securityDeposit: '',
            description: ''
          }];
        } else {
          // Ensure all pricing options have the new fields
          spaceData.pricingOptions = spaceData.pricingOptions.map(option => ({
            strategy: option.strategy || '',
            price: option.price || '',
            minHours: option.minHours || '',
            minGuests: option.minGuests || '',
            securityDeposit: option.securityDeposit || '',
            description: option.description || ''
          }));
        }
        
        setSpace(spaceData);
        
        // Handle image previews for editing
        if (editSpace.images && editSpace.images.length > 0) {
          // If images are File objects or have URLs
          setPreviewImages(
            editSpace.images.map(img => 
              img instanceof File ? URL.createObjectURL(img) : (img.url || img)
            )
          );
        } else {
          setPreviewImages([]);
        }
        
        if (editSpace.floorplanImage) {
          setPreviewFloorplan(
            editSpace.floorplanImage instanceof File 
              ? URL.createObjectURL(editSpace.floorplanImage) 
              : (editSpace.floorplanImage.url || editSpace.floorplanImage)
          );
        } else {
          setPreviewFloorplan(null);
        }
      } else {
        // For new spaces, start with one empty pricing option
        setSpace({
          ...initialState,
          pricingOptions: [{ 
            strategy: '', 
            price: '',
            minHours: '',
            minGuests: '',
            securityDeposit: '',
            description: ''
          }]
        });
        setPreviewImages([]);
        setPreviewFloorplan(null);
      }
      setErrors({});
    }
    
    // Cleanup URLs when component unmounts
    return () => {
      previewImages.forEach(img => {
        if (typeof img === 'string' && img.startsWith('blob:')) {
          URL.revokeObjectURL(img);
        }
      });
      
      if (previewFloorplan && typeof previewFloorplan === 'string' && previewFloorplan.startsWith('blob:')) {
        URL.revokeObjectURL(previewFloorplan);
      }
    };
  }, [open, editSpace]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSpace(prev => ({ ...prev, [name]: value }));
    
    // Clear error on field change
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleMultiSelectChange = (name, values) => {
    setSpace(prev => ({ ...prev, [name]: values }));
    
    // Clear error on field change
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleNumberInput = (e) => {
    // Allow only numeric input for number fields
    const { name, value } = e.target;
    // Allow empty or numeric values
    if (value === '' || /^[0-9]*$/.test(value)) {
      setSpace(prev => ({ ...prev, [name]: value }));
      
      // Clear error on field change
      if (errors[name]) {
        setErrors(prev => ({ ...prev, [name]: '' }));
      }
    }
  };
  
  const handleImageUpload = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const newImages = Array.from(e.target.files);
      
      // Create object URLs for preview
      const newPreviewUrls = newImages.map(file => URL.createObjectURL(file));
      setPreviewImages([...previewImages, ...newPreviewUrls]);
      
      // Update space state with new files
      setSpace(prev => ({
        ...prev,
        images: [...(prev.images || []), ...newImages]
      }));
      
      // Clear error if it exists
      if (errors.images) {
        setErrors(prev => ({ ...prev, images: '' }));
      }
    }
  };
  
  const handleFloorplanUpload = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      
      // Create object URL for preview
      if (previewFloorplan && previewFloorplan.startsWith('blob:')) {
        URL.revokeObjectURL(previewFloorplan);
      }
      
      const previewUrl = URL.createObjectURL(file);
      setPreviewFloorplan(previewUrl);
      
      // Update space state
      setSpace(prev => ({
        ...prev,
        floorplanImage: file
      }));
      
      // Clear error if it exists
      if (errors.floorplanImage) {
        setErrors(prev => ({ ...prev, floorplanImage: '' }));
      }
    }
  };
  
  const handleRemoveImage = (index) => {
    // Remove image and its preview
    if (previewImages[index] && previewImages[index].startsWith('blob:')) {
      URL.revokeObjectURL(previewImages[index]);
    }
    
    const newPreviewImages = [...previewImages];
    newPreviewImages.splice(index, 1);
    setPreviewImages(newPreviewImages);
    
    const newImages = [...space.images];
    newImages.splice(index, 1);
    setSpace(prev => ({
      ...prev,
      images: newImages
    }));
  };
  
  const handleRemoveFloorplan = () => {
    // Remove floorplan and its preview
    if (previewFloorplan && previewFloorplan.startsWith('blob:')) {
      URL.revokeObjectURL(previewFloorplan);
    }
    
    setPreviewFloorplan(null);
    setSpace(prev => ({
      ...prev,
      floorplanImage: null
    }));
  };

  // Handle updating individual pricing option
  const handlePricingOptionChange = (index, field, value) => {
    const updatedPricingOptions = [...space.pricingOptions];
    updatedPricingOptions[index] = {
      ...updatedPricingOptions[index],
      [field]: value
    };
    
    setSpace(prev => ({
      ...prev,
      pricingOptions: updatedPricingOptions
    }));
    
    // Clear error if it exists
    if (errors.pricingOptions) {
      setErrors(prev => ({ ...prev, pricingOptions: '' }));
    }
  };
  
  // Add new pricing option
  const addPricingOption = () => {
    setSpace(prev => ({
      ...prev,
      pricingOptions: [
        ...prev.pricingOptions,
        { 
          strategy: '', 
          price: '',
          minHours: '',
          minGuests: '',
          securityDeposit: '',
        }
      ]
    }));
  };
  
  // Remove pricing option
  const removePricingOption = (index) => {
    const updatedPricingOptions = [...space.pricingOptions];
    updatedPricingOptions.splice(index, 1);
    
    setSpace(prev => ({
      ...prev,
      pricingOptions: updatedPricingOptions
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!space.name.trim()) {
      newErrors.name = 'Space name is required';
    }
    
    if (!space.seatingCapacity) {
      newErrors.seatingCapacity = 'Seating capacity is required';
    }
    
    if (!space.standingCapacity) {
      newErrors.standingCapacity = 'Standing capacity is required';
    }
    
    if (!space.images || space.images.length === 0) {
      newErrors.images = 'At least one image is required';
    }
    
    if (!space.pricingOptions || space.pricingOptions.length === 0) {
      newErrors.pricingOptions = 'At least one pricing option is required';
    } else {
      const invalidPricingOptions = space.pricingOptions.some(
        option => !option.strategy || !option.price
      );
      
      if (invalidPricingOptions) {
        newErrors.pricingOptions = 'All pricing options must have a strategy and price';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      // Convert string numbers to actual numbers
      const formattedSpace = {
        ...space,
        seatingCapacity: Number(space.seatingCapacity),
        standingCapacity: Number(space.standingCapacity),
        pricingOptions: space.pricingOptions.map(option => ({
          strategy: option.strategy,
          price: Number(option.price),
          minHours: option.minHours ? Number(option.minHours) : null,
          minGuests: option.minGuests ? Number(option.minGuests) : null,
          securityDeposit: option.securityDeposit ? Number(option.securityDeposit) : null
        }))
      };
      
      onSubmit(formattedSpace);
    }
  };

  const isEditing = Boolean(editSpace);

  return (
    <Dialog 
      open={open} 
      onClose={isSubmitting ? null : onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        component: 'form',
        onSubmit: handleSubmit
      }}
    >
      <DialogTitle>
        {isEditing ? 'Edit Space' : 'Add New Space'}
        {!isSubmitting && (
          <Button
            aria-label="close"
            onClick={onClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
            startIcon={<CloseIcon />}
          >
            Close
          </Button>
        )}
      </DialogTitle>
      
      <DialogContent dividers>
        <Grid container spacing={3}>
          {/* Basic Information */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom fontWeight="bold">
              Basic Information
            </Typography>
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              name="name"
              label="Space Name"
              fullWidth
              required
              value={space.name}
              onChange={handleChange}
              error={!!errors.name}
              helperText={errors.name}
              disabled={isSubmitting}
              autoFocus
              placeholder="e.g., Grand Ballroom, Conference Room A"
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              name="description"
              label="Description"
              fullWidth
              multiline
              rows={3}
              value={space.description || ''}
              onChange={handleChange}
              disabled={isSubmitting}
              placeholder="Describe the space, its atmosphere, and key features..."
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              name="seatingCapacity"
              label="Seating Capacity"
              fullWidth
              required
              value={space.seatingCapacity}
              onChange={handleNumberInput}
              error={!!errors.seatingCapacity}
              helperText={errors.seatingCapacity}
              disabled={isSubmitting}
              InputProps={{
                endAdornment: <InputAdornment position="end">people</InputAdornment>,
              }}
              placeholder="Max number of seated guests"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              name="standingCapacity"
              label="Standing Capacity"
              fullWidth
              required
              value={space.standingCapacity}
              onChange={handleNumberInput}
              error={!!errors.standingCapacity}
              helperText={errors.standingCapacity}
              disabled={isSubmitting}
              InputProps={{
                endAdornment: <InputAdornment position="end">people</InputAdornment>,
              }}
              placeholder="Max number of standing guests"
            />
          </Grid>
          
          {/* Ideal Events */}
          <Grid item xs={12}>
            <FormControl fullWidth>
              <Autocomplete
                multiple
                id="idealEvents"
                options={IDEAL_EVENTS}
                value={space.idealEvents || []}
                onChange={(_, newValue) => handleMultiSelectChange('idealEvents', newValue)}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip 
                      label={option} 
                      {...getTagProps({ index })} 
                      key={option}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Ideal Events"
                    placeholder="Select types of events this space is ideal for"
                  />
                )}
                disabled={isSubmitting}
              />
            </FormControl>
          </Grid>
          
          {/* Images and Floorplan */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom fontWeight="bold">
              Images & Floorplan
            </Typography>
          </Grid>
          
          {/* Space Images */}
          <Grid item xs={12}>
            <Typography variant="body2" gutterBottom>
              Space Images <span style={{ color: 'red' }}>*</span>
            </Typography>
            <Box sx={{ mb: 2 }}>
              <input
                type="file"
                multiple
                accept="image/*"
                style={{ display: 'none' }}
                ref={imagesInputRef}
                onChange={handleImageUpload}
              />
              <Button
                variant="outlined"
                startIcon={<AddPhotoAlternateIcon />}
                onClick={() => imagesInputRef.current.click()}
                disabled={isSubmitting}
              >
                Add Images
              </Button>
              {errors.images && (
                <FormHelperText error>{errors.images}</FormHelperText>
              )}
            </Box>
            
            {previewImages.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                {previewImages.map((img, index) => (
                  <Box 
                    key={index} 
                    sx={{ 
                      position: 'relative',
                      width: 120,
                      height: 90,
                      borderRadius: 1,
                      overflow: 'hidden',
                      border: '1px solid #ccc'
                    }}
                  >
                    <IconButton
                      size="small"
                      sx={{ 
                        position: 'absolute', 
                        top: 2, 
                        right: 2,
                        backgroundColor: 'rgba(0,0,0,0.5)',
                        color: 'white',
                        '&:hover': {
                          backgroundColor: 'rgba(0,0,0,0.7)'
                        },
                        width: 24,
                        height: 24
                      }}
                      onClick={() => handleRemoveImage(index)}
                      disabled={isSubmitting}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                    <img 
                      src={img}
                      alt={`Space ${index + 1}`} 
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                    />
                  </Box>
                ))}
              </Box>
            )}
          </Grid>
          
          {/* Floorplan Image */}
          <Grid item xs={12}>
            <Typography variant="body2" gutterBottom>
              Floorplan Image
            </Typography>
            <Box sx={{ mb: 2 }}>
              <input
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                ref={floorplanInputRef}
                onChange={handleFloorplanUpload}
              />
              <Button
                variant="outlined"
                startIcon={<CloudUploadIcon />}
                onClick={() => floorplanInputRef.current.click()}
                disabled={isSubmitting}
              >
                Upload Floorplan
              </Button>
            </Box>
            
            {previewFloorplan && (
              <Box 
                sx={{ 
                  position: 'relative',
                  width: 200,
                  height: 150,
                  borderRadius: 1,
                  overflow: 'hidden',
                  border: '1px solid #ccc',
                  mb: 2
                }}
              >
                <IconButton
                  size="small"
                  sx={{ 
                    position: 'absolute', 
                    top: 2, 
                    right: 2,
                    backgroundColor: 'rgba(0,0,0,0.5)',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: 'rgba(0,0,0,0.7)'
                    },
                    width: 24,
                    height: 24
                  }}
                  onClick={handleRemoveFloorplan}
                  disabled={isSubmitting}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
                <img 
                  src={previewFloorplan}
                  alt="Floorplan" 
                  style={{ width: '100%', height: '100%', objectFit: 'contain' }} 
                />
              </Box>
            )}
          </Grid>
          
          {/* Pricing and Booking Requirements */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom fontWeight="bold">
              Pricing & Booking Requirements
            </Typography>
          </Grid>
          
          <Grid item xs={12}>
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="body2">
                  Pricing Options <span style={{ color: 'red' }}>*</span>
                </Typography>
                <Button 
                  size="small" 
                  variant="outlined" 
                  onClick={addPricingOption}
                  disabled={isSubmitting}
                >
                  Add Pricing Option
                </Button>
              </Box>
              
              {errors.pricingOptions && (
                <FormHelperText error sx={{ mb: 1 }}>{errors.pricingOptions}</FormHelperText>
              )}
              
              {space.pricingOptions.length === 0 && (
                <Box sx={{ textAlign: 'center', p: 2, border: '1px dashed #ccc', borderRadius: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    No pricing options added. Click "Add Pricing Option" to add your first pricing option.
                  </Typography>
                </Box>
              )}
              
              {space.pricingOptions.map((option, index) => (
                <Box 
                  key={index} 
                  sx={{ 
                    p: 2, 
                    mb: 2, 
                    border: '1px solid #e0e0e0', 
                    borderRadius: 1,
                    position: 'relative',
                    backgroundColor: '#f9f9f9'
                  }}
                >
                  <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => removePricingOption(index)}
                      disabled={isSubmitting || space.pricingOptions.length <= 1}
                      sx={{ p: 0.5 }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Pricing Option {index + 1}
                  </Typography>
                  
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth required>
                        <InputLabel id={`pricing-strategy-label-${index}`}>Pricing Strategy</InputLabel>
                        <Select
                          labelId={`pricing-strategy-label-${index}`}
                          value={option.strategy}
                          onChange={(e) => handlePricingOptionChange(index, 'strategy', e.target.value)}
                          label="Pricing Strategy"
                          disabled={isSubmitting}
                        >
                          {PRICING_STRATEGIES.map(strategy => (
                            <MenuItem key={strategy} value={strategy}>{strategy}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Price"
                        fullWidth
                        required
                        value={option.price}
                        onChange={(e) => {
                          // Allow only numeric input for price field
                          const value = e.target.value;
                          if (value === '' || /^[0-9]*$/.test(value)) {
                            handlePricingOptionChange(index, 'price', value);
                          }
                        }}
                        disabled={isSubmitting}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        }}
                        placeholder="Price amount"
                      />
                    </Grid>
                    
                    <Grid item xs={12} sm={4}>
                      <TextField
                        label="Minimum Hours"
                        fullWidth
                        value={option.minHours || ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === '' || /^[0-9]*$/.test(value)) {
                            handlePricingOptionChange(index, 'minHours', value);
                          }
                        }}
                        disabled={isSubmitting}
                        InputProps={{
                          endAdornment: <InputAdornment position="end">hours</InputAdornment>,
                        }}
                        placeholder="e.g., 2"
                      />
                    </Grid>
                    
                    <Grid item xs={12} sm={4}>
                      <TextField
                        label="Minimum Guests"
                        fullWidth
                        value={option.minGuests || ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === '' || /^[0-9]*$/.test(value)) {
                            handlePricingOptionChange(index, 'minGuests', value);
                          }
                        }}
                        disabled={isSubmitting}
                        InputProps={{
                          endAdornment: <InputAdornment position="end">guests</InputAdornment>,
                        }}
                        placeholder="e.g., 10"
                      />
                    </Grid>
                    
                    <Grid item xs={12} sm={4}>
                      <TextField
                        label="Security Deposit"
                        fullWidth
                        value={option.securityDeposit || ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === '' || /^[0-9]*$/.test(value)) {
                            handlePricingOptionChange(index, 'securityDeposit', value);
                          }
                        }}
                        disabled={isSubmitting}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        }}
                        placeholder="e.g., 500"
                      />
                    </Grid>
                  </Grid>
                </Box>
              ))}
            </Box>
          </Grid>
          
          {/* Tech Available */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom fontWeight="bold">
              Features & Technology
            </Typography>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <Autocomplete
                multiple
                id="techAvailable"
                options={TECH_AVAILABLE}
                value={space.techAvailable || []}
                onChange={(_, newValue) => handleMultiSelectChange('techAvailable', newValue)}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip 
                      label={option} 
                      {...getTagProps({ index })} 
                      key={option}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Technology Available"
                    placeholder="Select available technology"
                  />
                )}
                disabled={isSubmitting}
              />
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <Autocomplete
                multiple
                id="features"
                options={FEATURES}
                value={space.features || []}
                onChange={(_, newValue) => handleMultiSelectChange('features', newValue)}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip 
                      label={option} 
                      {...getTagProps({ index })} 
                      key={option}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Features"
                    placeholder="Select features"
                  />
                )}
                disabled={isSubmitting}
              />
            </FormControl>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button 
          onClick={onClose} 
          color="inherit"
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button 
          type="submit" 
          variant="contained" 
          color="primary"
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Saving...' : (isEditing ? 'Update Space' : 'Add Space')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SpaceForm; 